import { api, errorText } from "../api";
import { toast } from "../toast";
import type { Me, Playing, Row, Stage } from "../types";
import { EmptyState } from "./EmptyState";
import { QueueList, upNext } from "./Feed";
import { IconButton } from "./Icons";

/* What is sounding right now, and what is next — one card that is always there.

   Both halves used to be cards that appeared when they had something to say and
   vanished when they did not, which meant every clip that started or ended
   shoved the history down or yanked it back up, usually just as somebody was
   reaching for a row in it. Now the card is a fixed size whether it is empty or
   not, and what changes is only what is inside it: nothing that appears here
   can move anything else on the page.

   "Now playing" is a list rather than a single row: a character with its own
   OBS source plays on its own layer, so two of them can be talking at the same
   time, and a panel that showed only the most recent would quietly drop the
   other. */

const fail = (error: unknown) => toast(errorText(error), "error");

function skip(id: number) {
  api(`/api/requests/${id}/skip`, { method: "POST" })
    .then(() =>
      toast(`Skipped #${id}`, "", {
        label: "Undo",
        run: () =>
          void api(`/api/requests/${id}/unskip`, { method: "POST" }).catch(fail),
      }),
    )
    .catch(fail);
}

export function OnAir({
  playing,
  history,
  me,
  stageName,
  onReuse,
  onMuted,
}: {
  playing: Playing[];
  history: Row[];
  me: Me;
  stageName: (stage: Stage) => string;
  onReuse: (row: Row) => void;
  onMuted: () => void;
}) {
  const next = upNext(
    history,
    playing.map((entry) => entry.row.id),
  );
  const live = playing.length > 0;

  return (
    <div className="card on-air">
      <section className={"on-air-now" + (live ? " live" : "")} aria-live="polite">
        <h2>
          <span className={"wave" + (live ? "" : " still")} aria-hidden="true">
            <i></i>
            <i></i>
            <i></i>
            <i></i>
          </span>
          Now playing
        </h2>
        <div className="on-air-body">
          {!live ? (
            <EmptyState art="air" compact title="Nothing on air" />
          ) : (
            playing.map(({ stage, row }) => (
              <div className="playing" key={`${stage ?? "main"}-${row.id}`}>
                <div className="playing-top">
                  <b>{row.user}</b>
                  <span className="muted mono">
                    {row.voice_name} · #{row.id}
                  </span>
                  {/* Only worth naming once there is more than one place a clip
                      can come out of. */}
                  {stage !== null && <span className="badge">{stageName(stage)}</span>}
                  <span className="spacer"></span>
                  {me.admin && (
                    <IconButton
                      icon="skip"
                      title={`Skip #${row.id} on stream`}
                      onClick={() => skip(row.id)}
                    />
                  )}
                </div>
                <div className="playing-msg" title={row.text}>
                  {row.text}
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      <section className="on-air-next">
        <h2>
          Up next <span className="spacer"></span>
          <span className="muted">{next.length} waiting</span>
        </h2>
        <div className="on-air-body">
          {next.length === 0 ? (
            <EmptyState art="queue" compact title="Queue is empty" />
          ) : (
            <QueueList rows={next} me={me} onReuse={onReuse} onMuted={onMuted} />
          )}
        </div>
      </section>
    </div>
  );
}
